import { 
  BookOpen, 
  FileText, 
  Download, 
  ExternalLink,
  CheckSquare,
  AlertTriangle,
  Lightbulb,
  GraduationCap,
  Shield,
  FileCheck,
  Zap,
  Video
} from 'lucide-react';

interface ResourceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  type: string;
  link?: string;
  downloadable?: boolean;
}

function ResourceCard({ icon, title, description, type, link, downloadable }: ResourceCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
      <div className="flex items-start justify-between mb-4">
        <div className="p-3 bg-blue-100 rounded-lg">
          {icon}
        </div>
        <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-semibold rounded-full">
          {type}
        </span>
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-600 mb-4">{description}</p>
      <button
        onClick={() => link && window.open(link, '_blank')}
        className="flex items-center text-blue-600 hover:text-blue-700 font-semibold"
      >
        {downloadable ? (
          <>
            <Download className="w-4 h-4 mr-2" />
            Download
          </>
        ) : (
          <>
            <ExternalLink className="w-4 h-4 mr-2" />
            View Resource
          </>
        )}
      </button>
    </div>
  );
}

interface CodeReferenceProps {
  title: string;
  code: string;
  description: string;
  sections: { chapter: string; topic: string }[];
}

function CodeReference({ title, code, description, sections }: CodeReferenceProps) {
  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center mb-4">
        <div className="p-3 bg-green-100 rounded-lg mr-4">
          <BookOpen className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-600">{code}</p>
        </div>
      </div>
      <p className="text-slate-700 mb-4">{description}</p>
      <div className="border-t border-slate-200 pt-4">
        <h4 className="font-semibold text-slate-900 mb-3">Key Sections:</h4>
        <div className="space-y-2">
          {sections.map((section, index) => (
            <div key={index} className="flex items-start">
              <CheckSquare className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-1" />
              <div>
                <span className="font-medium text-slate-900">{section.chapter}</span>
                <span className="text-slate-600"> - {section.topic}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ResourcesPage() {
  const downloads = [
    {
      icon: <FileCheck className="w-6 h-6 text-blue-600" />,
      title: "Fire Alarm Design Checklist",
      description: "Comprehensive checklist for NFPA 72 compliant fire alarm system design. Covers all major requirements from device placement to documentation.",
      type: "PDF",
      downloadable: true,
      link: "#"
    },
    {
      icon: <FileText className="w-6 h-6 text-blue-600" />,
      title: "Battery Calculation Worksheet",
      description: "Excel template for calculating battery backup requirements per NFPA 72 Chapter 10. Includes formulas and safety factors.",
      type: "XLSX",
      downloadable: true,
      link: "#"
    },
    {
      icon: <FileText className="w-6 h-6 text-blue-600" />,
      title: "Notification Appliance Worksheet",
      description: "Calculate horn and strobe requirements based on room dimensions, ambient sound levels, and occupancy type.",
      type: "XLSX",
      downloadable: true,
      link: "#"
    },
    {
      icon: <FileCheck className="w-6 h-6 text-blue-600" />,
      title: "Plan Review Submittal Checklist",
      description: "Ensure your submittal package is complete before sending to the AHJ. Includes all required documents and drawings.",
      type: "PDF",
      downloadable: true,
      link: "#"
    },
    {
      icon: <FileText className="w-6 h-6 text-blue-600" />,
      title: "Device Spacing Quick Reference",
      description: "Quick reference guide for smoke detector, heat detector, and notification appliance spacing requirements.",
      type: "PDF",
      downloadable: true,
      link: "#"
    },
    {
      icon: <FileText className="w-6 h-6 text-blue-600" />,
      title: "Voltage Drop Calculator Template",
      description: "Excel template for calculating voltage drop on notification appliance circuits. Includes wire resistance tables.",
      type: "XLSX",
      downloadable: true,
      link: "#"
    }
  ];

  const codeReferences = [
    {
      title: "NFPA 72 - National Fire Alarm and Signaling Code",
      code: "2022 Edition",
      description: "The primary code governing fire alarm system design, installation, and testing in the United States.",
      sections: [
        { chapter: "Chapter 10", topic: "Fundamentals" },
        { chapter: "Chapter 17", topic: "Initiating Devices" },
        { chapter: "Chapter 18", topic: "Notification Appliances" },
        { chapter: "Chapter 21", topic: "Emergency Communications Systems" },
        { chapter: "Chapter 23", topic: "Protected Premises Control Units" },
        { chapter: "Chapter 24", topic: "Emergency Communications Systems" }
      ]
    },
    {
      title: "NEC Article 760 - Fire Alarm Systems",
      code: "2023 Edition",
      description: "National Electrical Code requirements for fire alarm system wiring, power supplies, and electrical installation.",
      sections: [
        { chapter: "Part I", topic: "General" },
        { chapter: "Part II", topic: "Non-Power-Limited Fire Alarm Circuits" },
        { chapter: "Part III", topic: "Power-Limited Fire Alarm Circuits" },
        { chapter: "760.41", topic: "Power Sources" },
        { chapter: "760.121", topic: "Power Sources for PLFA Circuits" },
        { chapter: "760.130", topic: "Wiring Methods" }
      ]
    },
    {
      title: "International Building Code (IBC)",
      code: "2021 Edition",
      description: "Building code requirements for fire alarm system installation based on occupancy classification and building characteristics.",
      sections: [
        { chapter: "Chapter 9", topic: "Fire Protection Systems" },
        { chapter: "Section 907", topic: "Fire Alarm and Detection Systems" },
        { chapter: "907.2", topic: "Where Required" },
        { chapter: "907.5", topic: "Manual Fire Alarm Boxes" },
        { chapter: "907.6", topic: "Installation and Monitoring" },
        { chapter: "Table 907.5.2.3.3", topic: "Audible Alarm Notification Appliances" }
      ]
    }
  ];

  const tutorials = [
    {
      icon: <GraduationCap className="w-6 h-6 text-purple-600" />,
      title: "Understanding NFPA 72 Chapter 18",
      description: "Complete guide to notification appliance requirements including audible and visible signaling.",
      type: "Guide"
    },
    {
      icon: <Video className="w-6 h-6 text-purple-600" />,
      title: "Fire Alarm System Design Basics",
      description: "Step-by-step tutorial on designing a compliant fire alarm system from start to finish.",
      type: "Tutorial"
    },
    {
      icon: <Lightbulb className="w-6 h-6 text-purple-600" />,
      title: "Common Plan Review Mistakes",
      description: "Learn about the most common errors that cause plan check corrections and how to avoid them.",
      type: "Guide"
    },
    {
      icon: <GraduationCap className="w-6 h-6 text-purple-600" />,
      title: "Battery Backup Sizing Explained",
      description: "Detailed explanation of NFPA 72 battery backup requirements and calculation methods.",
      type: "Guide"
    }
  ];

  const bestPractices = [
    {
      title: "Device Placement",
      tips: [
        "Follow manufacturer's spacing requirements from data sheets",
        "Consider ceiling height and configuration (smooth vs. beamed)",
        "Maintain proper clearances from HVAC diffusers and returns",
        "Document all spacing calculations in design notes"
      ]
    },
    {
      title: "Notification Appliances",
      tips: [
        "Calculate required sound pressure levels per NFPA 72 Chapter 18",
        "Ensure visible notification meets candela requirements",
        "Consider ambient sound levels and room acoustics",
        "Provide synchronization for all notification appliances"
      ]
    },
    {
      title: "Documentation",
      tips: [
        "Include complete riser diagrams with all circuit details",
        "Provide battery backup calculations with safety factors",
        "Create detailed device schedules with model numbers",
        "Include voltage drop calculations for all NAC circuits"
      ]
    },
    {
      title: "Code Compliance",
      tips: [
        "Verify occupancy classification with building official",
        "Check for local amendments to NFPA 72 and IBC",
        "Coordinate with other trades (sprinkler, HVAC, security)",
        "Review AHJ-specific submittal requirements early"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <BookOpen className="w-16 h-16 mx-auto mb-4" />
            <h1 data-testid="page-h1-resources" className="text-4xl md:text-5xl font-bold mb-4">
              Resources
            </h1>
            <p className="text-xl text-green-100 mb-8">
              Free downloads, code references, and educational materials for fire alarm professionals
            </p>
            <div className="flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 inline-flex">
              <Shield className="w-5 h-5 mr-2" />
              <span>All resources based on current NFPA 72 and NEC standards</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        {/* Downloads Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Free Downloads</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Professional templates and checklists to streamline your fire alarm design process
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {downloads.map((resource, index) => (
              <ResourceCard
                key={index}
                icon={resource.icon}
                title={resource.title}
                description={resource.description}
                type={resource.type}
                link={resource.link}
                downloadable={resource.downloadable}
              />
            ))}
          </div>
        </section>

        {/* Code References Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Code References</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Quick reference guides to the most important codes and standards
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {codeReferences.map((reference, index) => (
              <CodeReference
                key={index}
                title={reference.title}
                code={reference.code}
                description={reference.description}
                sections={reference.sections}
              />
            ))}
          </div>
        </section>

        {/* Tutorials Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Tutorials & Guides</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Educational content to enhance your fire alarm design knowledge
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tutorials.map((tutorial, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                <div className="p-3 bg-purple-100 rounded-lg inline-block mb-4">
                  {tutorial.icon}
                </div>
                <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-semibold rounded-full">
                  {tutorial.type}
                </span>
                <h3 className="text-lg font-bold text-slate-900 mt-3 mb-2">{tutorial.title}</h3>
                <p className="text-sm text-slate-600 mb-4">{tutorial.description}</p>
                <button className="flex items-center text-purple-600 hover:text-purple-700 font-semibold text-sm">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Read More
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Best Practices Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Best Practices</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Industry best practices for fire alarm system design and documentation
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {bestPractices.map((practice, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-yellow-100 rounded-lg mr-4">
                    <Lightbulb className="w-6 h-6 text-yellow-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{practice.title}</h3>
                </div>
                <ul className="space-y-3">
                  {practice.tips.map((tip, tipIndex) => (
                    <li key={tipIndex} className="flex items-start">
                      <Zap className="w-4 h-4 text-yellow-600 mr-2 flex-shrink-0 mt-1" />
                      <span className="text-slate-700">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Important Notice */}
        <section className="mb-16">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8">
            <div className="flex items-start">
              <AlertTriangle className="w-8 h-8 text-yellow-600 mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-bold text-yellow-900 mb-3">Important Notice</h3>
                <p className="text-yellow-800 mb-4">
                  These resources are provided for educational and reference purposes only. While we strive to ensure 
                  accuracy, codes and standards are regularly updated, and local jurisdictions may have specific 
                  requirements that differ from national standards.
                </p>
                <p className="text-yellow-800">
                  Always verify current code requirements with your local authority having jurisdiction (AHJ) and 
                  consult with licensed professionals for specific project requirements. Cal-Spec Fire Alarm Design 
                  is not responsible for any errors or omissions in these resources.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* External Resources */}
        <section>
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">External Resources</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Links to official standards organizations and industry resources
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a
              href="https://www.nfpa.org/codes-and-standards/all-codes-and-standards/list-of-codes-and-standards/detail?code=72"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 block"
            >
              <div className="p-3 bg-red-100 rounded-lg inline-block mb-4">
                <BookOpen className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">NFPA.org</h3>
              <p className="text-sm text-slate-600 mb-4">Official NFPA 72 documentation and updates</p>
              <span className="flex items-center text-red-600 font-semibold text-sm">
                <ExternalLink className="w-4 h-4 mr-2" />
                Visit Site
              </span>
            </a>

            <a
              href="https://www.nfpa.org/codes-and-standards/all-codes-and-standards/list-of-codes-and-standards/detail?code=70"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 block"
            >
              <div className="p-3 bg-blue-100 rounded-lg inline-block mb-4">
                <Zap className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">NEC Online</h3>
              <p className="text-sm text-slate-600 mb-4">National Electrical Code Article 760</p>
              <span className="flex items-center text-blue-600 font-semibold text-sm">
                <ExternalLink className="w-4 h-4 mr-2" />
                Visit Site
              </span>
            </a>

            <a
              href="https://codes.iccsafe.org/content/IBC2021P2"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 block"
            >
              <div className="p-3 bg-green-100 rounded-lg inline-block mb-4">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">ICC Codes</h3>
              <p className="text-sm text-slate-600 mb-4">International Building Code resources</p>
              <span className="flex items-center text-green-600 font-semibold text-sm">
                <ExternalLink className="w-4 h-4 mr-2" />
                Visit Site
              </span>
            </a>

            <a
              href="https://www.nicet.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 block"
            >
              <div className="p-3 bg-purple-100 rounded-lg inline-block mb-4">
                <GraduationCap className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">NICET</h3>
              <p className="text-sm text-slate-600 mb-4">Fire alarm certification and training</p>
              <span className="flex items-center text-purple-600 font-semibold text-sm">
                <ExternalLink className="w-4 h-4 mr-2" />
                Visit Site
              </span>
            </a>
          </div>
        </section>
      </div>

      {/* CTA Section */}
      <div className="bg-slate-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Need Professional Design Services?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Let our licensed engineers handle your fire alarm system design with expertise and precision
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="/services"
              className="inline-flex items-center px-8 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors duration-200"
            >
              View Our Services
            </a>
            <a 
              href="/contact"
              className="inline-flex items-center px-8 py-3 bg-white hover:bg-slate-100 text-slate-900 font-semibold rounded-lg transition-colors duration-200"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
